<template>
  <div v-if="hasSet" class="pqs px-6 pt-6 pb-6">
    <div class="d-flex align-center mb-1">
      <v-icon class="mr-3" color="primary">mdi-stethoscope</v-icon>
      <span class="text-h5 font-weight-bold">Provider questions</span>
    </div>
    <div class="text-caption text-medium-emphasis mb-4">
      The questions we ask AI to see which providers it names.
    </div>

    <v-alert v-if="!isActive && !loadError" type="info" variant="tonal" class="mb-4">
      <template v-if="needsProvider">Add a provider in the Providers tab to set up provider questions.</template>
      <template v-else>Finish setting up your provider questions in the Providers tab.</template>
      <template #append>
        <v-btn variant="text" size="small" @click="$emit('go-to-tab', 'providers')">Go to Providers</v-btn>
      </template>
    </v-alert>

    <v-alert v-if="loadError" type="error" variant="tonal" class="mb-4">
      {{ loadError }}
      <template #append>
        <v-btn variant="text" size="small" :loading="loading" @click="load">Retry</v-btn>
      </template>
    </v-alert>

    <v-card v-for="persona in shownPersonas" :key="persona.id" variant="outlined" class="persona-card mb-4">
      <v-card-text class="pa-5">
        <div class="pqs-head d-flex align-center mb-3">
          <v-icon class="mr-3" color="primary">mdi-account</v-icon>
          <span class="persona-query-title">{{ persona.name }}</span>
        </div>
        <div v-for="q in persona.questions" :key="q.id" class="pqs-row" :data-question-id="q.id">
          <div v-if="q.source_text" class="pqs-source text-medium-emphasis text-body-2">{{ q.source_text }}</div>
          <span class="pqs-text">{{ q.text }}</span>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { getProviderQuestions } from './providersApi'
import { errorMessage, errorCode } from './providerErrors'

export default {
  name: 'ProviderQuestionsSection',
  props: {
    reviewEntityId: { type: [Number, String], default: null },
    // The audit's provider list: its setup_stage tells us when the set changes elsewhere.
    providers: { type: Array, default: () => [] }
  },
  emits: ['go-to-tab'],
  data() {
    return { set: null, noSet: false, loadError: '', loading: false }
  },
  computed: {
    // Hidden until we know the audit has a set (and when it has none).
    hasSet() { return !!this.set || (!!this.loadError && !this.noSet) },
    isActive() { return this.set?.stage === 'active' },
    // A skipped or failed set restarts when a provider is added; until then the Providers
    // tab shows only its empty state, so say what to do there.
    needsProvider() { return this.set?.stage === 'failed' && !(this.providers?.length) },
    shownPersonas() { return this.set?.personas || [] },
    listStage() { return this.providers?.[0]?.setup_stage || null }
  },
  watch: {
    // The set moved on in the Providers tab (confirmed, skipped, generated): reload it.
    // Also recovers from a 404: the audit gained a set while this section was already mounted.
    listStage(now, was) {
      if (!now || now === was || this.loading) return
      if (this.noSet || (this.set && now !== this.set.stage)) this.load({ keep: true })
    },
    reviewEntityId: { immediate: true, handler() { this.load() } }
  },
  methods: {
    async fetchSet(id) {
      const data = await getProviderQuestions(id)
      return data.provider_questions || null
    },
    // keep: reload the same audit's set in place, without blanking the list first.
    async load({ keep = false, followUp = false } = {}) {
      const id = this.reviewEntityId
      this.loadError = ''; this.noSet = false
      if (!keep) this.set = null
      if (!id) { this.loading = false; return }
      this.loading = true
      const current = () => id === this.reviewEntityId
      try {
        const set = await this.fetchSet(id)
        if (!current()) return
        this.set = set
      } catch (e) {
        if (!current()) return
        if (errorCode(e) === 'no_question_set') { this.noSet = true; this.set = null }
        else this.loadError = errorMessage(e, "Couldn't load provider questions.")
      } finally {
        if (current()) this.loading = false
      }
      // The list's stage moved while we were reading: read once more (never chained).
      const now = this.listStage
      if (!followUp && current() && now && now !== this.set?.stage && !this.loadError) {
        await this.load({ keep: true, followUp: true })
      }
    }
  }
}
</script>

<style scoped>
.pqs-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 0; border-top: 1px solid #ece7da; }
.pqs-head + .pqs-row { border-top: 0; }
.pqs-source { flex-basis: 100%; }
.pqs-text { flex: 1 1 260px; font-size: 15px; }
</style>
